import { Button, Card, Col, Form, Grid, Image, Input, Row } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CalendarBlankFilled from "../../../../assets/images/icons/CalendarBlankFilled";
import ClockFilled from "../../../../assets/images/icons/ClockFilled";
import TransactionError from "../../../../assets/images/icons/TransactionError";
import TransactionFailed from "../../../../assets/images/icons/TransactionFailed";
import TransactionSuccess from "../../../../assets/images/icons/TransactionSuccess";
import { CompanionForm, CustomMessage } from "../../../../components";
import { H6, P1, P2, P3 } from "../../../../components/NormalFonts";
import { designatedCoach } from "../../../../enumerations/designatedCoach";
import { sessionType } from "../../../../enumerations/sessionType";
import { useUpdateCompanion } from "../../../../hooks/Checkout/checkout.hooks";
import { useGetUserOrderDetail } from "../../../../hooks/Order/order.hooks";
import {
  SearchResponse,
  SearchSessionDetail,
} from "../../../../hooks/Search/data";
import useGetTimezone from "../../../../hooks/Timezone/timezone.hooks";
import { moneyFormat } from "../../../../plugins/numberFormat";
import { paymentStatusEnum } from "../../enumerations/paymentStatusEnum";
import { DataEntryProps } from "./data";
import styles from "./style.module.scss";

const DataEntry: React.FC<DataEntryProps> = ({
  // searchResult,
  paymentNumber,
  paymentStatus,
}) => {
  const { t } = useTranslation();
  const { toLocalTimezone, formatDate } = useGetTimezone();
  const [loading, setLoading] = useState<boolean>(false);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { TextArea } = Input;
  const navigate = useNavigate();

  const { mutateAsync: updateCompanion } = useUpdateCompanion();
  const { data: userOrderDetail } = useGetUserOrderDetail(
    { payment_number: paymentNumber || "" },
    { enabled: !!paymentNumber && paymentStatus === paymentStatusEnum.SUCCESS }
  );

  const renderDate = (sessions: SearchSessionDetail[]) => {
    if (sessions.length <= 1) {
      return formatDate(toLocalTimezone(sessions[0].date));
    }
    return `${formatDate(
      toLocalTimezone(sessions[0].date)
    )} - ${toLocalTimezone(sessions[sessions.length - 1].date)?.format(
      `MM${t("normal.month")}DD${t("normal.date")}`
    )}`;
  };

  const TITLE_DESC = {
    [designatedCoach.NON_DESIGNATED]: [
      {
        title: t("recordDetail.skiResort"),
        render: (record: SearchResponse) => (
          <P1 weight="medium" color="var(--text-content-1)">
            {t(`skiresort.${record.ski_resort_code}`)}
          </P1>
        ),
      },
      {
        title: t("normal.skill.title"),
        render: (record: SearchResponse) => (
          <P1 weight="medium" color="var(--text-content-1)">
            {t(`normal.skill.${record.skill_code}`)}
          </P1>
        ),
      },
      {
        title: t("normal.count.title"),
        render: (record: SearchResponse) => (
          <>
            <P1 weight="medium" color="var(--text-content-1)">
              {t("recordDetail.adults", { count: record.adults })}
            </P1>
            <P1 weight="medium" color="var(--text-content-1)">
              {t("recordDetail.kids", { count: record.kids })}
            </P1>
          </>
        ),
      },
      {
        title: t("search.summary"),
        render: (record: SearchResponse) => (
          <P1 weight="medium" color="var(--text-content-1)">
            {`${t(`currency.${process.env.REACT_APP_CURRENCY}`)}${moneyFormat(
              record.total_price
            )}`}
          </P1>
        ),
      },
    ],
    [designatedCoach.DESIGNATED]: [
      {
        title: t("home.searchbar.date.title"),
        render: (record: SearchResponse) => (
          <P1 weight="medium" color="var(--text-content-1)">
            {renderDate(record.session_details || [])}
          </P1>
        ),
      },
      {
        title: t("recordDetail.skiResort"),
        render: (record: SearchResponse) => (
          <P1 weight="medium" color="var(--text-content-1)">
            {t(`skiresort.${record.ski_resort_code}`)}
          </P1>
        ),
      },
      {
        title: t("normal.skill.title"),
        render: (record: SearchResponse) => (
          <P1 weight="medium" color="var(--text-content-1)">
            {t(`normal.skill.${record.skill_code}`)}
          </P1>
        ),
      },
      {
        title: t("normal.count.title"),
        render: (record: SearchResponse) => (
          <>
            <P1 weight="medium" color="var(--text-content-1)">
              {t("recordDetail.adults", { count: record.adults })}
            </P1>
            <P1 weight="medium" color="var(--text-content-1)">
              {t("recordDetail.kids", { count: record.kids })}
            </P1>
          </>
        ),
      },
      {
        title: t("search.summary"),
        render: (record: SearchResponse) => (
          <P1 weight="medium" color="var(--text-content-1)">
            {`${t(`currency.${process.env.REACT_APP_CURRENCY}`)}${moneyFormat(
              record.total_price
            )}`}
          </P1>
        ),
      },
    ],
  };

  const getSpan = () => {
    if (screens.lg) return 6;
    if (screens.sm) return 8;
    return 12; // Default for xs or smaller
  };

  const renderTitle = (result: SearchResponse) => {
    return (
      <div className={styles.titleContainer}>
        <H6
          style={{ marginBottom: 16 }}
          weight="medium"
          color="var(--text-content-1)"
        >
          {result.coach_nickname
            ? t("normal.coach.nickname", { nickname: result.coach_nickname })
            : renderDate(result.session_details || [])}
          {/* {t(`skiresort.${result.ski_resort_code}`)} */}
        </H6>
        <Row gutter={[32, 8]}>
          {TITLE_DESC[result.coach_designated].map((desc: any) => {
            return (
              <Col span={getSpan()}>
                <P2 color="var(--text-content-3)">{desc.title}</P2>
                {desc.render(result)}
              </Col>
            );
          })}
        </Row>
      </div>
    );
  };

  const convertToArray = (input: any) => {
    const result: any = {
      remark: input.remark || "",
      companion: [],
    };

    let index = 0;
    while (`first_name_${index}` in input) {
      result.companion.push({
        first_name: input[`first_name_${index}`],
        last_name: input[`last_name_${index}`],
        nickname: input[`nickname_${index}`],
        skill_level_code: input[`skill_level_code${index}`],
        age: input[`age_${index}`],
      });
      index++;
    }

    return result;
  };

  const onFinish = async (value: any, queryResult: SearchResponse) => {
    setLoading(true);
    const request = {
      company_code: process.env.REACT_APP_COMPANY_CODE,
      order_number: queryResult.order_number,
      ...convertToArray(value),
    };
    console.debug("request", request);
    await updateCompanion(
      { params: request },
      {
        onSuccess: async (data: any) => {
          console.debug("data", data);
          setLoading(false);
          navigate("/record");
        },
        onError: (error: any) => {
          CustomMessage({
            alertMessage: t([
              `error.${error.data.result.code}`,
              error.data.result.message,
            ]),
            alertType: "error",
          });
          setLoading(false);
        },
      }
    );
  };

  const renderForm = (result: SearchResponse, index: number) => {
    return (
      <div key={index}>
        <div className={styles.formTitle}>
          <P1 weight="medium" color="var(--text-content-1)">
            {t(`checkout.data.student.${index + 1}`)}
          </P1>
        </div>
        <CompanionForm
          index={index}
          className={styles.formBody}
          skillCode={result.skill_code}
        />
      </div>
    );
  };

  const renderSessionTime = (session: SearchSessionDetail) => {
    let result = "";
    switch (session.session_type) {
      case sessionType.ALL_DAY:
        result += t("search.fullday");
        break;
      case sessionType.MORNING:
        result += t("search.morning");
        break;
      case sessionType.AFTERNOON:
        result += t("search.afternoon");
        break;
      default:
        result += t("search.fullday");
    }
    result += ` ${session.class_start_at}~${session.class_end_at}`;
    return result;
  };

  const renderSession = (session: SearchSessionDetail[] | undefined) => {
    if (!session) return;
    return session.map((_session: SearchSessionDetail, index: number) => {
      // return <SessionItem data={_session} key={`session_${index}`} />;
      return (
        <div key={`session_${index}`} className={styles.sessionWrapper}>
          <Image
            src={_session.session_img}
            preview={false}
            width={72}
            height={72}
            style={{ objectFit: "cover", borderRadius: 8 }}
          />
          <div className={styles.dataWrapper}>
            <P2 color="var(--text-content-1)">{_session.session_name}</P2>
            <div className={styles.dateWrapper}>
              <CalendarBlankFilled
                style={{ fontSize: 14, color: "var(--text-content2)" }}
              />
              <P2 color="var(--text-content-1)">
                {formatDate(toLocalTimezone(_session.date))}
              </P2>
            </div>
            <div className={styles.dateWrapper}>
              <ClockFilled
                style={{ fontSize: 14, color: "var(--text-content2)" }}
              />
              <P2 color="var(--text-content-1)">
                {renderSessionTime(_session)}
              </P2>
            </div>
          </div>
        </div>
      );
    });
  };

  const renderContent = () => {
    if (
      paymentStatus === paymentStatusEnum.FAILED ||
      paymentStatus === paymentStatusEnum.ERROR
    ) {
      return (
        <>
          <P1 color="var(--text-content-1)" style={{ paddingTop: "48px" }}>
            {t("transaction.errormessage")}
          </P1>
          <Button type="primary" onClick={() => navigate("/record")}>
            {t("transaction.gotorecord")}
          </Button>
        </>
      );
    }
    return userOrderDetail?.order_details?.map(
      (result: SearchResponse, index: number) => {
        if (result.session_details?.length <= 0) return null;
        return (
          <Card
            key={`order_data_${index}`}
            style={{ width: "100%" }}
            styles={{ body: { padding: 0 } }}
            size={screens.sm ? "default" : "small"}
            title={renderTitle(result)}
            variant="borderless"
          >
            {renderSession(result.session_details)}
            <H6
              className={styles.dataTitle}
              weight="medium"
              color="var(--text-content-1)"
            >
              {t("checkout.data.studentinfo.title")}
            </H6>
            <Form
              autoComplete={"off"}
              onFinish={(value: any) => onFinish(value, result)}
              // form={form}
              // requiredMark={false}
              layout={"vertical"}
            >
              {Array.from({ length: result.adults + result.kids }).map(
                (_, index) => {
                  return renderForm(result, index);
                }
              )}
              <H6
                className={styles.dataTitle}
                weight="medium"
                color="var(--text-content-1)"
              >
                {t("checkout.data.remark.title")}
              </H6>
              <div className={styles.formTitle}>
                <P1 weight="medium" color="var(--text-content-1)">
                  {t("checkout.data.notes.title")}
                </P1>
              </div>
              <Form.Item name="remark">
                <div className={styles.remarkWrapper}>
                  <TextArea
                    maxLength={300}
                    showCount
                    placeholder={t("checkout.data.remark.hint")}
                  />
                </div>
              </Form.Item>
              <div className={styles.buttonContainer}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {t("normal.save")}
                </Button>
              </div>
            </Form>
          </Card>
        );
      }
    );
  };

  const renderStatusImage = () => {
    switch (paymentStatus) {
      case paymentStatusEnum.SUCCESS:
        return (
          <TransactionSuccess
            style={{ color: "var(--success)", fontSize: 52, marginBottom: 8 }}
          />
        );
      case paymentStatusEnum.FAILED:
        return (
          <TransactionFailed
            style={{ color: "var(--error)", fontSize: 52, marginBottom: 8 }}
          />
        );
      case paymentStatusEnum.ERROR:
        return (
          <TransactionError
            style={{ color: "var(--warn)", fontSize: 52, marginBottom: 8 }}
          />
        );

      default:
        break;
    }
  };

  return (
    <div
      className={`${styles.dataEntryContainer} ${
        screens.sm ? "" : styles.mobile
      }`}
    >
      <div className={styles.header}>
        {renderStatusImage()}
        <P1 weight="medium" color="var(--text-content-2)">
          {t(`transaction.${paymentStatus}`)}
        </P1>
        <P3>{t("transaction.transid", { id: paymentNumber })}</P3>
      </div>
      {paymentStatus === paymentStatusEnum.SUCCESS && (
        <div className={styles.infoWrapper}>
          <H6 weight="medium" color="var(--text-content-1)">
            {t("checkout.data.info")}
          </H6>
          <ol style={{ paddingInlineStart: 14 }}>
            <li>
              <P2 color="var(--text-content-1)">{t("checkout.data.desc.1")}</P2>
            </li>
            {/* <li>
              <P2 color="var(--text-content-1)">{t("checkout.data.desc.2")}</P2>
            </li> */}
          </ol>
        </div>
      )}
      <>{renderContent()}</>
    </div>
  );
};

export default DataEntry;
